import React from "react";
import { Reorder, useDragControls } from "framer-motion";
import { X, Play, GripVertical, Film, Music, Volume2, ChevronsLeftRight, ArrowLeftRight } from "lucide-react";
import { resolveMediaUrl } from "@/components/FileUpload";
import { NEUTRAL_POSTER } from "@/lib/neutralPoster";
import { MusicPicker } from "@/components/MusicPicker";
import { Switch } from "@/components/ui/switch";
import { UploadOverlay, MusicVolumeButtons } from "./ComposerBits";
import { FxButton } from "@/components/media/MediaFxEditor";
import { fxStyle, hasFx } from "@/lib/mediaFx";

// Aperçus des médias d'une publication — partagés par la feuille PC et le plein écran téléphone.

// Vignette de la bande horizontale : le glisser ne part QUE de la poignée ⋮⋮ (touch-none) ; un balayage ailleurs fait
// défiler la bande jusqu'à la dernière image, la croix reste un simple tap.
const StripThumb = ({ a, i, removeAttachment, t }) => {
  const controls = useDragControls();
  return (
    <Reorder.Item
      as="div" value={a} dragListener={false} dragControls={controls}
      className="relative shrink-0 h-20 w-20 rounded-lg overflow-hidden border bg-black select-none"
      style={{ borderColor: "var(--yalola-border)" }}
      data-testid={`composer-album-thumb-${i}`}
    >
      {a.kind === "video" ? (
        <>
          <video src={a.localUrl || resolveMediaUrl(a.url)} poster={a.localPoster || (a.posterUrl ? resolveMediaUrl(a.posterUrl) : NEUTRAL_POSTER)} muted playsInline preload="metadata" className="h-full w-full object-cover pointer-events-none" style={fxStyle(a.fx)} />
          <span className="absolute inset-0 grid place-items-center pointer-events-none"><Play size={16} className="text-white" /></span>
        </>
      ) : (
        <img src={a.localUrl || resolveMediaUrl(a.url)} alt="" draggable={false} className="h-full w-full object-cover pointer-events-none" />
      )}
      <span className="absolute bottom-0.5 left-0.5 h-5 min-w-5 px-1.5 rounded-full bg-black/70 text-white text-[11px] font-bold grid place-items-center pointer-events-none" data-testid={`composer-album-thumb-num-${i}`}>{i + 1}</span>
      <button type="button" onPointerDown={(e) => controls.start(e)} className="absolute bottom-0.5 right-0.5 z-20 grid place-items-center h-7 w-7 rounded-full bg-black/70 text-white cursor-grab active:cursor-grabbing touch-none" aria-label={t("composer.drag_handle", "Déplacer")} data-testid={`composer-album-thumb-handle-${i}`}><GripVertical size={14} /></button>
      <button type="button" onClick={() => removeAttachment(a.id)} className="absolute top-1 right-1 z-20 grid place-items-center h-6 w-6 rounded-full bg-black/65 text-white hover:bg-black/85" data-testid={`composer-album-thumb-remove-${i}`}><X size={13} /></button>
    </Reorder.Item>
  );
};

// Grande carte de la liste verticale : poignée ⋮⋮ N en haut à gauche = seule zone de glisser ; le reste défile la page.
const AlbumRow = ({ a, i, removeAttachment, setCaption, uploadAttachment, onEditFx, beforeAfter = false, t }) => {
  const controls = useDragControls();
  return (
    <Reorder.Item as="div" value={a} dragListener={false} dragControls={controls} className="relative bg-black select-none" data-testid={`composer-album-item-${i}`} data-ba-role={beforeAfter ? (i === 0 ? "before" : "after") : undefined}>
      {beforeAfter && (
        <span className="absolute top-2 left-1/2 -translate-x-1/2 z-20 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide text-white pointer-events-none" style={{ background: "var(--yalola-primary)" }} data-testid={`composer-ba-badge-${i}`}>
          {i === 0 ? t("post.before", "Avant") : t("post.after", "Après")}
        </span>
      )}
      {a.kind === "video" ? (
        <>
          <video
            src={a.localUrl || resolveMediaUrl(a.url)}
            poster={a.localPoster || (a.posterUrl ? resolveMediaUrl(a.posterUrl) : NEUTRAL_POSTER)}
            muted playsInline preload="metadata"
            className="w-full block max-h-[420px] object-contain bg-black pointer-events-none"
            style={fxStyle(a.fx)}
          />
          <span className="absolute inset-0 grid place-items-center pointer-events-none">
            <span className="h-12 w-12 rounded-full grid place-items-center" style={{ background: "rgba(0,0,0,0.5)" }}>
              <Play size={22} className="text-white ml-0.5" />
            </span>
          </span>
        </>
      ) : (
        <img src={a.localUrl || resolveMediaUrl(a.url)} alt="" draggable={false} className="w-full block max-h-[420px] object-contain bg-black pointer-events-none" />
      )}
      <UploadOverlay att={a} t={t} onRetry={() => uploadAttachment(a)} />
      <button type="button" onPointerDown={(e) => controls.start(e)} className="absolute top-2 left-2 z-20 flex items-center gap-1 rounded-full bg-black/60 text-white pl-1.5 pr-2.5 h-9 cursor-grab active:cursor-grabbing touch-none" aria-label={t("composer.drag_handle", "Déplacer")} data-testid={`composer-album-drag-${i}`}>
        <GripVertical size={16} />
        <span className="text-sm font-bold leading-none" data-testid={`composer-album-num-${i}`}>{i + 1}</span>
      </button>
      {onEditFx && <FxButton onClick={() => onEditFx(a)} active={hasFx(a.fx)} className="absolute top-2 right-11" testId={`composer-album-fx-${i}`} t={t} />}
      <button
        type="button"
        onClick={() => removeAttachment(a.id)}
        className="absolute top-2 right-2 z-20 rounded-full bg-black/60 text-white p-1 hover:bg-black/80"
        data-testid={`media-remove-${i}`}
      ><X size={14} /></button>
      <div className="px-2.5 py-1.5 flex items-center gap-1.5" style={{ background: "var(--yalola-surface)" }}>
        <input
          type="text"
          value={a.caption || ""}
          onChange={(e) => setCaption(a.id, e.target.value)}
          placeholder={t("composer.caption_placeholder", "Ajouter une légende…")}
          maxLength={200}
          className="w-full bg-transparent text-sm outline-none placeholder:text-[color:var(--yalola-text-muted)]"
          style={{ color: "var(--yalola-text)" }}
          data-testid={`composer-album-caption-${i}`}
        />
      </div>
    </Reorder.Item>
  );
};

// Album (2+ médias) : bande de vignettes réordonnable (3+) + liste verticale avec légende par média.
export const AlbumPreviews = ({ visualAtts, reorderVisuals, removeAttachment, setCaption, uploadAttachment, onEditFx, layout = null, setLayout, canBeforeAfter = false, t }) => (
  <div className="mt-2" data-testid="composer-album-previews">
    {canBeforeAfter && setLayout && (
      <div className="mb-2 rounded-xl border px-3 py-2" style={{ borderColor: layout === "before_after" ? "var(--yalola-primary)" : "var(--yalola-border)", background: layout === "before_after" ? "var(--yalola-primary-soft)" : "transparent" }} data-testid="composer-before-after" data-on={layout === "before_after" ? "1" : "0"}>
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-semibold flex items-center gap-1.5" style={{ color: "var(--yalola-text)" }}>
            <ChevronsLeftRight size={15} style={{ color: "var(--yalola-primary)" }} />
            {t("composer.before_after", "Avant / Après")}
          </span>
          <Switch checked={layout === "before_after"} onCheckedChange={(v) => setLayout(v ? "before_after" : null)} data-testid="composer-before-after-switch" />
        </div>
        {layout === "before_after" && (
          <div className="mt-1.5 flex items-center justify-between gap-2">
            <span className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }} data-testid="composer-before-after-hint">{t("composer.before_after_hint", "Compare les deux : la 1re = avant, la 2e = après.")}</span>
            <button type="button" onClick={() => reorderVisuals([visualAtts[1], visualAtts[0]])} className="shrink-0 inline-flex items-center gap-1 rounded-full border px-2.5 h-7 text-[11px] font-semibold" style={{ borderColor: "var(--yalola-border)", color: "var(--yalola-text)", background: "var(--yalola-surface)" }} data-testid="composer-before-after-swap">
              <ArrowLeftRight size={12} /> {t("composer.before_after_swap", "Inverser")}
            </button>
          </div>
        )}
      </div>
    )}
    {visualAtts.length >= 3 && (
      <div className="mb-2" data-testid="composer-album-reorder-strip">
        <div className="text-[11px] font-medium mb-1" style={{ color: "var(--yalola-text-muted)" }}>
          {t("composer.reorder_strip_hint", "Réorganisez vite — glissez les vignettes par la poignée ⋮⋮")}
        </div>
        <Reorder.Group as="div" axis="x" values={visualAtts} onReorder={reorderVisuals} className="flex gap-1.5 overflow-x-auto pb-1 select-none" style={{ WebkitOverflowScrolling: "touch" }}>
          {visualAtts.map((a, i) => <StripThumb key={a.id} a={a} i={i} removeAttachment={removeAttachment} t={t} />)}
        </Reorder.Group>
      </div>
    )}
    <div className="flex items-center gap-1.5 mb-1.5 text-[11px] font-medium" style={{ color: "var(--yalola-text-muted)" }} data-testid="composer-album-reorder-hint">
      <GripVertical size={13} /> {t("composer.reorder_hint", "Maintiens la poignée ⋮⋮ d'un média et glisse pour changer l'ordre")}
    </div>
    <Reorder.Group
      as="div"
      axis="y"
      values={visualAtts}
      onReorder={reorderVisuals}
      className="rounded-xl overflow-hidden border select-none"
      style={{ borderColor: "var(--yalola-border)" }}
    >
      {visualAtts.map((a, i) => <AlbumRow key={a.id} a={a} i={i} removeAttachment={removeAttachment} setCaption={setCaption} uploadAttachment={uploadAttachment} onEditFx={onEditFx} beforeAfter={canBeforeAfter && layout === "before_after"} t={t} />)}
    </Reorder.Group>
  </div>
);

// Média unique : grande photo ou vidéo (badge Reel quand la vidéo courte sera publiée en Reel).
export const SinglePreview = ({ imageAtts, videoAtt, effectiveIsReel, onVideoMeta, previewVideoRef, removeAttachment, uploadAttachment, onEditFx, t }) => (
  <>
    {imageAtts.length === 1 && (
      <div className="mt-2" data-testid="composer-image-previews">
        <div
          className="relative rounded-xl overflow-hidden border"
          style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface-alt)" }}
          data-testid="composer-image-preview-0"
        >
          <img src={imageAtts[0].localUrl || resolveMediaUrl(imageAtts[0].url)} alt="" className="w-full max-h-[340px] object-contain" />
          <UploadOverlay att={imageAtts[0]} t={t} onRetry={() => uploadAttachment(imageAtts[0])} />
          {onEditFx && <FxButton onClick={() => onEditFx(imageAtts[0])} className="absolute top-1.5 left-1.5" testId="composer-fx-0" t={t} />}
          <button
            type="button"
            onClick={() => removeAttachment(imageAtts[0].id)}
            className="absolute top-1.5 right-1.5 z-20 rounded-full bg-black/60 text-white p-1 hover:bg-black/80"
            data-testid="media-remove-0"
          ><X size={13} /></button>
        </div>
      </div>
    )}

    {videoAtt && (
      <div className="relative mt-2 rounded-xl overflow-hidden bg-black" data-testid="composer-video-preview">
        <video
          ref={previewVideoRef}
          src={videoAtt.localUrl || resolveMediaUrl(videoAtt.url)}
          poster={videoAtt.localPoster || (videoAtt.posterUrl ? resolveMediaUrl(videoAtt.posterUrl) : NEUTRAL_POSTER)}
          controls
          playsInline
          preload="metadata"
          onLoadedMetadata={onVideoMeta}
          className={effectiveIsReel ? "mx-auto object-contain" : "w-full aspect-video object-contain"}
          style={{ ...(effectiveIsReel ? { aspectRatio: "9 / 16", maxHeight: "360px" } : {}), ...(fxStyle(videoAtt.fx) || {}) }}
        />
        <UploadOverlay att={videoAtt} t={t} onRetry={() => uploadAttachment(videoAtt)} />
        {effectiveIsReel && (
          <span className="absolute top-2 left-2 z-20 chip" style={{ background: "var(--yalola-primary)", color: "white", borderColor: "transparent" }} data-testid="composer-reel-badge">
            <Film size={12} /> Reel
          </span>
        )}
        {onEditFx && <FxButton onClick={() => onEditFx(videoAtt)} active={hasFx(videoAtt.fx)} className={`absolute top-2 ${effectiveIsReel ? "left-20" : "left-2"}`} testId="composer-fx-video" t={t} />}
        <button
          type="button"
          onClick={() => removeAttachment(videoAtt.id)}
          className="absolute top-2 right-2 z-20 rounded-full bg-black/60 text-white px-2 py-1 text-xs"
          data-testid="composer-remove-video"
        >
          {t("composer.remove", "Retirer")}
        </button>
      </div>
    )}
  </>
);

// Publication audio seule : lecteur + état d'envoi.
export const AudioPreview = ({ audioAtt, removeAttachment, uploadAttachment, t }) => (
  <div className="relative mt-2 rounded-xl border p-3 flex items-center gap-2" style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface-alt)" }} data-testid="composer-audio-preview">
    <Music size={18} style={{ color: "var(--yalola-primary)" }} className="shrink-0" />
    <audio controls src={audioAtt.localUrl || resolveMediaUrl(audioAtt.url)} className="w-full h-10" />
    {audioAtt.status === "uploading" && (
      <span className="text-[11px] shrink-0 font-medium" style={{ color: "var(--yalola-primary)" }} data-testid="composer-audio-progress">
        {audioAtt.progress < 100 ? `${audioAtt.progress}%` : t("composer.processing", "Traitement...")}
      </span>
    )}
    {audioAtt.status === "error" && (
      <button type="button" onClick={() => uploadAttachment(audioAtt)} className="text-xs underline shrink-0 text-red-600" data-testid="composer-audio-retry">
        {t("composer.retry", "Réessayer")}
      </button>
    )}
    <button type="button" onClick={() => removeAttachment(audioAtt.id)} className="text-xs underline shrink-0" style={{ color: "var(--yalola-text-muted)" }} data-testid="composer-remove-audio">
      {t("composer.remove", "Retirer")}
    </button>
  </div>
);

// Musique de fond façon story sur UN SEUL média (jamais pour un album) + son de la vidéo + volume.
export const MusicSection = ({ music, setMusic, musicVolume, setMusicVolume, keepVideoSound, setKeepVideoSound, videoAtt, previewVideoRef, t, testId = "composer-music" }) => (
  <div className="mt-2 space-y-2" data-testid={`${testId}-section`}>
    <MusicPicker music={music} onChange={setMusic} previewVolume={musicVolume} onPreview={(playing) => {
      const v = previewVideoRef?.current;
      if (!v) return;
      if (playing) { try { v.muted = !keepVideoSound; v.loop = true; v.currentTime = 0; v.play().catch(() => {}); } catch { /* noop */ } }
      else { try { v.pause(); } catch { /* noop */ } }
    }} t={t} testId={testId} />
    {videoAtt && music && (
      <div className="flex items-center justify-between rounded-lg border px-3 py-2" style={{ borderColor: "var(--yalola-border)" }}>
        <span className="text-xs font-medium flex items-center gap-1.5">
          <Volume2 size={14} style={{ color: "var(--yalola-primary)" }} />
          {t("composer.keep_video_sound", "Garder le son de la vidéo")}
        </span>
        <Switch checked={keepVideoSound} onCheckedChange={setKeepVideoSound} data-testid={`${testId.replace(/-music$/, "")}-keep-video-sound`} />
      </div>
    )}
    {music && (
      <div className="flex items-center justify-between rounded-lg border px-3 py-2 gap-2" style={{ borderColor: "var(--yalola-border)" }}>
        <span className="text-xs font-medium flex items-center gap-1.5 shrink-0">
          <Volume2 size={14} style={{ color: "var(--yalola-primary)" }} />
          {t("composer.music_volume", "Volume de la musique")}
        </span>
        <MusicVolumeButtons musicVolume={musicVolume} setMusicVolume={setMusicVolume} t={t} testId={`${testId}-volume`} />
      </div>
    )}
  </div>
);

// Aperçu de lien façon Facebook (YouTube / Facebook / article) avec bouton pour le retirer.
export const ComposerLinkPreview = ({ linkPreview, onRemove, t }) => (
  <div className="mt-2 rounded-xl overflow-hidden border relative" style={{ borderColor: "var(--yalola-border)" }} data-testid="composer-link-preview">
    <button
      type="button"
      onClick={onRemove}
      className="absolute top-2 right-2 z-20 rounded-full bg-black/60 text-white p-1.5 hover:bg-black/80"
      data-testid="composer-link-preview-remove"
    ><X size={14} /></button>
    <div className="relative">
      {linkPreview.thumbnail ? (
        <img src={linkPreview.thumbnail} alt="" className="w-full aspect-video object-cover" />
      ) : linkPreview.kind !== "article" ? (
        <div className="w-full aspect-video grid place-items-center" style={{ background: linkPreview.provider === "facebook" ? "linear-gradient(135deg, #1877F2 0%, #0e4da3 100%)" : "linear-gradient(135deg, #7f1d1d 0%, #450a0a 100%)" }}>
          <div className="flex flex-col items-center gap-2 text-white">
            <span className="h-12 w-12 rounded-full bg-white/20 grid place-items-center"><Play size={22} className="ml-0.5" /></span>
            <span className="text-sm font-semibold">{linkPreview.provider === "facebook" ? t("embed.fb_video", "Vidéo Facebook") : "Vidéo"}</span>
          </div>
        </div>
      ) : null}
    </div>
    <div className="px-3 py-2" style={{ background: "var(--yalola-surface-alt)" }}>
      <div className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>
        {linkPreview.kind === "article" ? (linkPreview.domain || "").toUpperCase() : linkPreview.provider === "youtube" ? "YOUTUBE.COM" : "FACEBOOK.COM"}
      </div>
      <div className="text-sm font-semibold truncate" data-testid="composer-link-title">{linkPreview.title || linkPreview.url}</div>
      {linkPreview.author && <div className="text-xs truncate" style={{ color: "var(--yalola-text-muted)" }}>{linkPreview.author}</div>}
      {linkPreview.kind === "article" && linkPreview.description && (
        <div className="text-xs mt-0.5 line-clamp-2" style={{ color: "var(--yalola-text-muted)" }} data-testid="composer-link-description">{linkPreview.description}</div>
      )}
    </div>
  </div>
);
